import { withDependencies, named } from '@wix/thunderbolt-ioc'
import {
	CarmiInstance,
	IPageAssetsLoader,
	PageAssets,
	EditorFeatureConfigSymbol,
	FeatureName,
	FeaturesConfig,
	ILogger,
	LoggerSymbol,
	LOADING_PHASES,
} from '@wix/thunderbolt-symbols'
import { ByRefResolvingDoneSymbol, IByRefResolvingDone } from 'ds-feature-viewer-manager-by-ref-api'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import { name } from '../symbols'
import type { AssetsLoaderEditorConfig } from '../types'
import _ from 'lodash'

export const PageAssetsLoader = withDependencies<IPageAssetsLoader>(
	[CarmiInstance, named(EditorFeatureConfigSymbol, name), ByRefResolvingDoneSymbol, LoggerSymbol],
	(
		{ pageFeaturesConfigs, masterPageConfigs, navigation, waitForBatchesApplied }: DSCarmi,
		{ components, pageCompIdToPageType, props, stateRefs }: AssetsLoaderEditorConfig,
		{ isByRefResolvingDone }: IByRefResolvingDone,
		logger: ILogger
	) => {
		const getAssets = (pageCompId: string, pageConfigs: FeaturesConfig) => {
			const isByRefResolvingDonePromise = waitForBatchesApplied().then(() => isByRefResolvingDone())
			return {
				css: isByRefResolvingDonePromise,
				// TODO: Fix tests to return structure
				components: isByRefResolvingDonePromise.then(() => _.cloneDeep(components[pageCompId] || {})),
				features: isByRefResolvingDonePromise.then(() => Object.keys(pageConfigs) as Array<FeatureName>),
				props: isByRefResolvingDonePromise.then(() => ({
					render: {
						compProps: _.cloneDeep(props[pageCompId]),
					},
					...pageConfigs,
				})),
				stateRefs: isByRefResolvingDonePromise.then(() => _.cloneDeep(stateRefs[pageCompId])),
				siteFeaturesConfigs: isByRefResolvingDonePromise.then(() =>
					pageCompId === 'masterPage' ? masterPageConfigs : {}
				),
			}
		}

		const load = (pageCompId: string): PageAssets => {
			if (pageCompId === 'masterPage') {
				logger.phaseStarted(LOADING_PHASES.DS_LOAD_MASTER_PAGE_ASSETS)
			} else {
				logger.phaseStarted(LOADING_PHASES.DS_LOAD_PAGE_ASSETS)
			}
			const pageType = pageCompIdToPageType[pageCompId]
			if (!pageType) {
				navigation.updateLogicalPageId(pageCompId)
				const pageConfigs = pageFeaturesConfigs.logicalPage

				return getAssets(pageCompId, pageConfigs)
			}
			const pageConfigs = pageFeaturesConfigs[pageType]
			const assets = getAssets(pageCompId, pageConfigs)
			if (pageCompId === 'masterPage') {
				logger.phaseEnded(LOADING_PHASES.DS_LOAD_MASTER_PAGE_ASSETS)
			} else {
				logger.phaseEnded(LOADING_PHASES.DS_LOAD_PAGE_ASSETS)
			}

			return assets
		}

		return {
			load,
		}
	}
)
